import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CLAUDE_MARK, GPT_MARK } from './FlowDiagram';
import './flowry.css';

type AssistantId = 'claude' | 'gpt';
type PartKind = 'text' | 'bullet' | 'quote' | 'check';
type SourceState = 'idle' | 'checking' | 'done';

type Scenario = {
  id: string;
  assistant: AssistantId;
  sources: string[];
  teamKey: string;
  questionKey: string;
  parts: { kind: PartKind; key: string }[];
};

const ASSISTANTS: Record<AssistantId, { name: string; color: string; tile: string; path: string }> = {
  claude: { name: 'Claude', color: '#D97757', tile: '#FBF1EC', path: CLAUDE_MARK },
  gpt: { name: 'GPT', color: '#0E0F0E', tile: '#F2F2EE', path: GPT_MARK },
};

const SCENARIOS: Scenario[] = [
  {
    id: 'sales',
    assistant: 'claude',
    sources: ['CRM', 'Email', 'ERP'],
    teamKey: 'usecase_sales_team',
    questionKey: 'usecase_sales_q',
    parts: [
      { kind: 'text', key: 'usecase_sales_1' },
      { kind: 'bullet', key: 'usecase_sales_2' },
      { kind: 'bullet', key: 'usecase_sales_3' },
      { kind: 'bullet', key: 'usecase_sales_4' },
      { kind: 'text', key: 'usecase_sales_5' },
    ],
  },
  {
    id: 'operations',
    assistant: 'gpt',
    sources: ['ERP', 'Inventory', 'Projects'],
    teamKey: 'usecase_ops_team',
    questionKey: 'usecase_ops_q',
    parts: [
      { kind: 'text', key: 'usecase_ops_1' },
      { kind: 'bullet', key: 'usecase_ops_2' },
      { kind: 'bullet', key: 'usecase_ops_3' },
      { kind: 'bullet', key: 'usecase_ops_4' },
      { kind: 'text', key: 'usecase_ops_5' },
    ],
  },
  {
    id: 'service',
    assistant: 'claude',
    sources: ['Helpdesk', 'ERP', 'Shipping'],
    teamKey: 'usecase_service_team',
    questionKey: 'usecase_service_q',
    parts: [
      { kind: 'text', key: 'usecase_service_1' },
      { kind: 'quote', key: 'usecase_service_2' },
    ],
  },
  {
    id: 'workflows',
    assistant: 'gpt',
    sources: ['ERP', 'Accounting', 'Email'],
    teamKey: 'usecase_workflows_team',
    questionKey: 'usecase_workflows_q',
    parts: [
      { kind: 'text', key: 'usecase_workflows_1' },
      { kind: 'check', key: 'usecase_workflows_2' },
      { kind: 'check', key: 'usecase_workflows_3' },
    ],
  },
];

const CHAR_MS = 34;
const SEND_PAUSE = 380;
const SOURCES_GAP = 320;
const PER_SOURCE = 420;
const SOURCES_TAIL = 280;
const ANSWER_CHAR_MS = 13;
const HOLD_MS = 4200;
const FADE_MS = 450;

function answerLength(parts: { text: string }[]) {
  return parts.reduce((sum, part) => sum + part.text.length, 0);
}

function timeline(question: string, parts: { text: string }[], sourceCount: number) {
  const sentAt = question.length * CHAR_MS + SEND_PAUSE;
  const sourcesStart = sentAt + SOURCES_GAP;
  const sourcesEnd = sourcesStart + sourceCount * PER_SOURCE + SOURCES_TAIL;
  const answerEnd = sourcesEnd + answerLength(parts) * ANSWER_CHAR_MS;
  return { sentAt, sourcesStart, sourcesEnd, answerEnd, end: answerEnd + HOLD_MS };
}

function AssistantMark({ assistant, size }: { assistant: AssistantId; size: number }) {
  const icon = ASSISTANTS[assistant];
  return (
    <span className="usecase-mark" style={{ width: size, height: size, backgroundColor: icon.tile }}>
      <svg viewBox="0 0 24 24" width={size * 0.57} height={size * 0.57} aria-hidden="true">
        <path d={icon.path} fill={icon.color} />
      </svg>
    </span>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" className="usecase-check-icon" aria-hidden="true">
      <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const UseCaseChat = () => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const elapsedRef = useRef(0);
  const durationRef = useRef(0);
  const [index, setIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [visible, setVisible] = useState(false);
  const [reduced, setReduced] = useState(false);

  const scenario = SCENARIOS[index];
  const team = t(scenario.teamKey);
  const question = t(scenario.questionKey);
  const parts = useMemo(
    () => scenario.parts.map((part) => ({ kind: part.kind, text: t(part.key) })),
    [scenario, t]
  );
  const script = useMemo(
    () => timeline(question, parts, scenario.sources.length),
    [question, parts, scenario.sources.length]
  );
  const assistant = ASSISTANTS[scenario.assistant];

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.35 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  durationRef.current = reduced ? 7000 : script.end;

  useEffect(() => {
    if (!visible) return;
    let last = performance.now();
    const id = window.setInterval(() => {
      const now = performance.now();
      elapsedRef.current += now - last;
      last = now;
      if (elapsedRef.current >= durationRef.current) {
        elapsedRef.current = 0;
        setIndex((current) => (current + 1) % SCENARIOS.length);
      }
      setElapsed(elapsedRef.current);
    }, 40);
    return () => window.clearInterval(id);
  }, [visible]);

  useEffect(() => {
    const rail = tabsRef.current;
    const tab = rail?.children[index] as HTMLElement | undefined;
    if (!rail || !tab || rail.scrollWidth <= rail.clientWidth) return;
    rail.scrollTo({ left: tab.offsetLeft - 4, behavior: reduced ? 'auto' : 'smooth' });
  }, [index, reduced]);

  const playhead = reduced ? script.answerEnd : elapsed;
  const typedChars = Math.min(question.length, Math.floor(playhead / CHAR_MS));
  const sent = playhead >= script.sentAt;
  const showSources = playhead >= script.sourcesStart;
  const sourceStates: SourceState[] = scenario.sources.map((_, sourceIndex) => {
    const start = script.sourcesStart + sourceIndex * PER_SOURCE;
    if (playhead >= start + PER_SOURCE) return 'done';
    if (playhead >= start) return 'checking';
    return 'idle';
  });
  const totalAnswer = answerLength(parts);
  const answerChars = Math.max(0, Math.min(totalAnswer, Math.floor((playhead - script.sourcesEnd) / ANSWER_CHAR_MS)));
  const answering = answerChars > 0 && answerChars < totalAnswer;
  const fading = !reduced && playhead >= script.end - FADE_MS;
  const progress = Math.min(1, elapsed / durationRef.current);

  const visibleParts: { kind: PartKind; text: string }[] = [];
  let remaining = answerChars;
  for (const part of parts) {
    if (remaining <= 0) break;
    visibleParts.push({ kind: part.kind, text: part.text.slice(0, remaining) });
    remaining -= part.text.length;
  }

  const select = (next: number) => {
    elapsedRef.current = 0;
    setElapsed(0);
    setIndex(next);
  };

  return (
    <section className="usecase-section" id="use-cases" aria-labelledby="usecases-title">
      <p className="usecase-eyebrow">{t('usecases_eyebrow')}</p>
      <h2 id="usecases-title">{t('usecases_title')}</h2>
      <div className="usecase-grid" ref={rootRef}>
        <div className="usecase-tabs" ref={tabsRef} role="group" aria-label={t('usecases_eyebrow')}>
          {SCENARIOS.map((item, itemIndex) => {
            const active = itemIndex === index;
            return (
              <button
                key={item.id}
                type="button"
                className={`usecase-tab${active ? ' is-active' : ''}`}
                aria-pressed={active}
                onClick={() => select(itemIndex)}
              >
                <span className="usecase-tab-label">
                  {t(item.teamKey)}
                  <span className="usecase-tab-icon">
                    <AssistantMark assistant={item.assistant} size={20} />
                  </span>
                </span>
                <span className="usecase-progress" aria-hidden="true" style={{ opacity: active ? 1 : 0 }}>
                  <span style={{ width: `${active ? progress * 100 : 0}%` }} />
                </span>
              </button>
            );
          })}
        </div>

        <div className={`usecase-panel${fading ? ' is-fading' : ''}`} aria-hidden="true">
          <div className="usecase-panel-head">
            <div className="usecase-panel-who">
              <AssistantMark assistant={scenario.assistant} size={28} />
              <span className="usecase-panel-name">{assistant.name}</span>
              <span className="usecase-panel-team">· {team}</span>
            </div>
            <span className="usecase-connected">
              <span className="usecase-connected-dot" />
              {t('usecase_connected')}
            </span>
          </div>
          <div className="usecase-thread" key={scenario.id}>
            {sent && <div className="usecase-question flow-fade-up">{question}</div>}
            {showSources && (
              <div className="usecase-sources flow-fade-up">
                <span>{sourceStates.every((state) => state === 'done') ? t('usecase_checked') : t('usecase_checking')}</span>
                {scenario.sources.map((source, sourceIndex) => (
                  <span key={source} className={`usecase-source is-${sourceStates[sourceIndex]}`}>
                    {sourceStates[sourceIndex] === 'done' ? <CheckIcon /> : <span className="usecase-source-dot" />}
                    {source}
                  </span>
                ))}
              </div>
            )}
            {visibleParts.length > 0 && (
              <div className="usecase-reply">
                <AssistantMark assistant={scenario.assistant} size={26} />
                <div className="usecase-reply-body">
                  {visibleParts.map((part, partIndex) => {
                    const cursor = answering && partIndex === visibleParts.length - 1 ? <span className="usecase-caret" /> : null;
                    if (part.kind === 'bullet') {
                      return (
                        <p className="usecase-bullet" key={`${part.kind}-${partIndex}`}>
                          <span />
                          <span>{part.text}{cursor}</span>
                        </p>
                      );
                    }
                    if (part.kind === 'quote') {
                      return <p className="usecase-quote" key={`${part.kind}-${partIndex}`}>{part.text}{cursor}</p>;
                    }
                    if (part.kind === 'check') {
                      return (
                        <p className="usecase-check" key={`${part.kind}-${partIndex}`}>
                          <CheckIcon />
                          <span>{part.text}{cursor}</span>
                        </p>
                      );
                    }
                    return <p key={`${part.kind}-${partIndex}`}>{part.text}{cursor}</p>;
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="usecase-composer">
            <div className="usecase-composer-bar">
              <span className="usecase-composer-text">
                {!sent && typedChars > 0 ? (
                  <span className="is-typing">{question.slice(0, typedChars)}<span className="usecase-caret" /></span>
                ) : (
                  <span className="is-placeholder">{t('usecase_ask', { assistant: assistant.name })}</span>
                )}
              </span>
              <span className={`usecase-send${!sent && typedChars > 0 ? ' is-ready' : ''}`}>
                <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                  <path d="M8 13V3m-4 4l4-4 4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        <ul className="sr-only">
          {SCENARIOS.map((item) => {
            const itemParts = item.parts.map((part) => t(part.key));
            return (
              <li key={item.id}>
                {t('usecase_sr', {
                  team: t(item.teamKey),
                  assistant: ASSISTANTS[item.assistant].name,
                  question: t(item.questionKey),
                  sources: item.sources.join(', '),
                  answer: itemParts.join(' '),
                })}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default UseCaseChat;
